import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
import DialogModal from '../../components/DialogModal';
import libraryService from '../../services/libraryService';
import classesService from '../../services/classesService';
import LibraryItemForm from '../../components/library/LibraryItemForm';
import LibraryCatalog from '../../components/library/LibraryCatalog';
import CirculationPanel from '../../components/library/CirculationPanel';
import { Plus, ChartBar, Tag as TagIcon, FolderSimple } from '@phosphor-icons/react';

const SORT_OPTIONS = [
  { value: 'views', label: 'Most viewed' },
  { value: 'downloads', label: 'Most downloaded' },
  { value: 'rating', label: 'Top rated' },
  { value: 'newest', label: 'Newest' },
];

function LibraryAdminPage() {
  const [pageTab, setPageTab] = useState('catalog');
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState([]);

  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [promptDialog, setPromptDialog] = useState(null); // { title, message, defaultValue, onSubmit }

  const [categoryName, setCategoryName] = useState('');
  const [categoryDescription, setCategoryDescription] = useState('');
  const [categoryParentId, setCategoryParentId] = useState('');

  const [analyticsSort, setAnalyticsSort] = useState('views');
  const [analyticsItems, setAnalyticsItems] = useState([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const [circulationReport, setCirculationReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  const [acquisitions, setAcquisitions] = useState([]);
  const [acquisitionForm, setAcquisitionForm] = useState({ title: '', author: '', isbn: '', quantity_requested: 1 });

  const loadLookups = useCallback(async () => {
    try {
      const [categoriesData, tagsData, subjectsData, classesData] = await Promise.all([
        libraryService.getCategories(),
        libraryService.getTags(),
        classesService.listAllSubjects().catch(() => []),
        classesService.listClasses().catch(() => []),
      ]);
      setCategories(categoriesData || []);
      setTags(tagsData || []);
      setSubjects(subjectsData || []);
      setClasses(classesData || []);
    } catch (error) {
      toast.error(error.message || 'Failed to load library filters');
    }
  }, []);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setAnalyticsLoading(true);
        const data = await libraryService.getItems({ sort_by: analyticsSort, limit: 10, page: 1 });
        if (!cancelled) setAnalyticsItems(data.items || []);
      } catch (error) {
        if (!cancelled) toast.error(error.message || 'Failed to load analytics');
      } finally {
        if (!cancelled) setAnalyticsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [analyticsSort, refreshKey]);

  const loadCirculationReport = useCallback(async () => {
    try {
      setReportLoading(true);
      const data = await libraryService.getCirculationReport();
      setCirculationReport(data);
    } catch (error) {
      toast.error(error.message || 'Failed to load circulation report');
    } finally {
      setReportLoading(false);
    }
  }, []);

  const loadAcquisitions = useCallback(async () => {
    try {
      const data = await libraryService.getAcquisitions();
      setAcquisitions(data || []);
    } catch (error) {
      toast.error(error.message || 'Failed to load acquisitions');
    }
  }, []);

  useEffect(() => {
    if (pageTab === 'reports') loadCirculationReport();
    if (pageTab === 'acquisitions') loadAcquisitions();
  }, [pageTab, loadCirculationReport, loadAcquisitions]);

  const handleCreateAcquisition = async (e) => {
    e.preventDefault();
    if (!acquisitionForm.title.trim()) {
      toast.error('Title is required');
      return;
    }
    try {
      await libraryService.createAcquisition({
        ...acquisitionForm,
        quantity_requested: Number(acquisitionForm.quantity_requested) || 1,
        author: acquisitionForm.author.trim() || null,
        isbn: acquisitionForm.isbn.trim() || null,
      });
      toast.success('Acquisition request created');
      setAcquisitionForm({ title: '', author: '', isbn: '', quantity_requested: 1 });
      loadAcquisitions();
    } catch (error) {
      toast.error(error.message || 'Unable to create acquisition request');
    }
  };

  const handleReceiveAcquisition = (acquisition) => {
    const remaining = acquisition.quantity_requested - acquisition.quantity_received;
    setPromptDialog({
      title: 'Receive Copies',
      message: `How many copies arrived? (up to ${remaining})`,
      defaultValue: String(remaining),
      onSubmit: async (value) => {
        if (!value) return;
        const quantity = Number(value);
        if (!quantity || quantity <= 0) return;
        try {
          await libraryService.receiveAcquisition(acquisition.id, { quantity_received: quantity });
          toast.success('Copies received into the catalog');
          loadAcquisitions();
        } catch (error) {
          toast.error(error.response?.data?.detail || error.message || 'Unable to receive copies');
        }
      },
    });
  };

  const handleCancelAcquisition = async (acquisition) => {
    try {
      await libraryService.cancelAcquisition(acquisition.id);
      toast.success('Acquisition request cancelled');
      loadAcquisitions();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Unable to cancel');
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    if (!categoryName.trim()) {
      toast.error('Category name is required');
      return;
    }
    try {
      await libraryService.createCategory({
        name: categoryName.trim(),
        description: categoryDescription.trim() || null,
        parent_id: categoryParentId || null,
      });
      toast.success('Category created');
      setCategoryName('');
      setCategoryDescription('');
      setCategoryParentId('');
      loadLookups();
    } catch (error) {
      toast.error(error.message || 'Unable to create category');
    }
  };

  const handleDeleteCategory = async (category) => {
    try {
      await libraryService.deleteCategory(category.id);
      toast.success('Category deleted');
      loadLookups();
    } catch (error) {
      toast.error(error.message || 'Unable to delete category');
    }
  };

  const handleDeleteTag = async (tag) => {
    try {
      await libraryService.deleteTag(tag.id);
      toast.success('Tag deleted');
      loadLookups();
    } catch (error) {
      toast.error(error.message || 'Unable to delete tag');
    }
  };

  const openCreateForm = () => {
    setEditingItem(null);
    setShowForm(true);
  };

  const openEditForm = (item) => {
    setEditingItem(item);
    setShowForm(true);
  };

  const handleSaved = () => {
    setShowForm(false);
    setEditingItem(null);
    setRefreshKey((k) => k + 1);
    loadLookups();
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await libraryService.deleteItem(deleteTarget.id);
      toast.success('Resource deleted');
      setDeleteTarget(null);
      setRefreshKey((k) => k + 1);
    } catch (error) {
      toast.error(error.message || 'Delete failed');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Layout>
      <div className="page" style={{ display: 'grid', gap: 16 }}>
        <div className="page-header">
          <div>
            <h1>E-Library Management</h1>
            <p>Create and organize digital learning resources for students and teachers.</p>
          </div>
          {pageTab === 'catalog' && (
            <button className="btn btn-primary" type="button" onClick={openCreateForm}>
              <Plus size={16} /> Add Resource
            </button>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className={pageTab === 'catalog' ? 'btn btn-primary' : 'btn btn-secondary'} onClick={() => setPageTab('catalog')}>Catalog</button>
          <button type="button" className={pageTab === 'circulation' ? 'btn btn-primary' : 'btn btn-secondary'} onClick={() => setPageTab('circulation')}>Circulation Desk</button>
          <button type="button" className={pageTab === 'reports' ? 'btn btn-primary' : 'btn btn-secondary'} onClick={() => setPageTab('reports')}>Circulation Reports</button>
          <button type="button" className={pageTab === 'acquisitions' ? 'btn btn-primary' : 'btn btn-secondary'} onClick={() => setPageTab('acquisitions')}>Acquisitions</button>
        </div>

        {pageTab === 'circulation' && <CirculationPanel />}

        {pageTab === 'reports' && (
          <div className="card" style={{ padding: 16, display: 'grid', gap: 16 }}>
            {reportLoading && <div className="empty-state">Loading report...</div>}
            {!reportLoading && circulationReport && (
              <>
                <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
                  {[
                    ['Checkouts', circulationReport.checkouts_total],
                    ['Returns', circulationReport.returns_total],
                    ['Active loans', circulationReport.active_loans],
                    ['Overdue', circulationReport.overdue_count],
                  ].map(([label, value]) => (
                    <div key={label} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>{label}</div>
                      <div style={{ fontSize: 24, fontWeight: 700 }}>{value}</div>
                    </div>
                  ))}
                </div>
                <div style={{ color: '#6b7280', fontSize: 12 }}>{circulationReport.start_date} to {circulationReport.end_date}</div>

                <div>
                  <div style={{ fontWeight: 700, marginBottom: 8 }}>Most borrowed titles</div>
                  <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                    <tbody>
                      {circulationReport.most_borrowed.map((row) => (
                        <tr key={row.item_id} style={{ borderTop: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '6px 0' }}>{row.title}</td>
                          <td style={{ textAlign: 'right' }}>{row.checkouts} checkout{row.checkouts === 1 ? '' : 's'}</td>
                        </tr>
                      ))}
                      {circulationReport.most_borrowed.length === 0 && (
                        <tr><td className="empty-state">No checkouts in this period.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div>
                  <div style={{ fontWeight: 700, marginBottom: 8 }}>Overdue loans</div>
                  <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ textAlign: 'left', color: '#6b7280' }}>
                        <th style={{ paddingBottom: 6 }}>Title</th>
                        <th>Borrower</th>
                        <th>Due date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {circulationReport.overdue_loans.map((loan) => (
                        <tr key={loan.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '6px 0' }}>{loan.item_title}</td>
                          <td>{loan.borrower_name}</td>
                          <td>{loan.due_date}</td>
                        </tr>
                      ))}
                      {circulationReport.overdue_loans.length === 0 && (
                        <tr><td colSpan={3} className="empty-state">No overdue loans.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {pageTab === 'acquisitions' && (
          <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'minmax(280px, 340px) 1fr' }}>
            <div className="card" style={{ padding: 16, display: 'grid', gap: 10 }}>
              <div style={{ fontWeight: 700 }}>Request a new title</div>
              <form onSubmit={handleCreateAcquisition} style={{ display: 'grid', gap: 8 }}>
                <input className="form-control" placeholder="Title" value={acquisitionForm.title} onChange={(e) => setAcquisitionForm({ ...acquisitionForm, title: e.target.value })} />
                <input className="form-control" placeholder="Author" value={acquisitionForm.author} onChange={(e) => setAcquisitionForm({ ...acquisitionForm, author: e.target.value })} />
                <input className="form-control" placeholder="ISBN" value={acquisitionForm.isbn} onChange={(e) => setAcquisitionForm({ ...acquisitionForm, isbn: e.target.value })} />
                <input className="form-control" type="number" min="1" placeholder="Quantity" value={acquisitionForm.quantity_requested} onChange={(e) => setAcquisitionForm({ ...acquisitionForm, quantity_requested: e.target.value })} />
                <button className="btn btn-secondary" type="submit">Request</button>
              </form>
            </div>
            <div className="card" style={{ padding: 16 }}>
              <div style={{ fontWeight: 700, marginBottom: 10 }}>Acquisition requests</div>
              <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: '#6b7280' }}>
                    <th style={{ paddingBottom: 6 }}>Title</th>
                    <th>Qty</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {acquisitions.map((a) => (
                    <tr key={a.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '6px 0' }}>{a.title}{a.author ? ` — ${a.author}` : ''}</td>
                      <td>{a.quantity_received}/{a.quantity_requested}</td>
                      <td style={{ textTransform: 'capitalize' }}>{a.status}</td>
                      <td style={{ display: 'flex', gap: 6 }}>
                        {(a.status === 'requested' || a.status === 'ordered') && (
                          <button className="btn btn-secondary" style={{ padding: '2px 8px', fontSize: 12 }} type="button" onClick={() => handleReceiveAcquisition(a)}>Receive</button>
                        )}
                        {a.status !== 'received' && a.status !== 'cancelled' && (
                          <button className="btn btn-danger" style={{ padding: '2px 8px', fontSize: 12 }} type="button" onClick={() => handleCancelAcquisition(a)}>Cancel</button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {acquisitions.length === 0 && (
                    <tr><td colSpan={4} className="empty-state">No acquisition requests yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {pageTab === 'catalog' && (
        <>
        <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
          <div className="card" style={{ padding: 16, display: 'grid', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700 }}>
              <FolderSimple size={18} /> Manage Categories
            </div>
            <form onSubmit={handleCreateCategory} style={{ display: 'grid', gap: 8 }}>
              <input className="form-control" placeholder="New category" value={categoryName} onChange={(e) => setCategoryName(e.target.value)} />
              <input className="form-control" placeholder="Description" value={categoryDescription} onChange={(e) => setCategoryDescription(e.target.value)} />
              <select className="form-control" value={categoryParentId} onChange={(e) => setCategoryParentId(e.target.value)}>
                <option value="">No parent (top-level)</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <button className="btn btn-secondary" type="submit">Add Category</button>
            </form>
            <div style={{ display: 'grid', gap: 6, maxHeight: 220, overflow: 'auto' }}>
              {categories.map((c) => (
                <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, borderBottom: '1px solid #f3f4f6', paddingBottom: 4 }}>
                  <span>{c.parent_id ? `— ${c.name}` : c.name}</span>
                  <button className="btn btn-danger" type="button" style={{ padding: '2px 8px', fontSize: 12 }} onClick={() => handleDeleteCategory(c)}>Delete</button>
                </div>
              ))}
              {categories.length === 0 && <div className="empty-state">No categories yet.</div>}
            </div>
          </div>

          <div className="card" style={{ padding: 16, display: 'grid', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700 }}>
              <TagIcon size={18} /> Manage Tags
            </div>
            <div style={{ color: '#6b7280', fontSize: 13 }}>Tags are created automatically when added to a resource.</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', maxHeight: 220, overflow: 'auto' }}>
              {tags.map((t) => (
                <span key={t.id} className="badge" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {t.name}
                  <button type="button" onClick={() => handleDeleteTag(t)} style={{ background: 'none', border: 0, cursor: 'pointer', color: '#dc2626' }}>×</button>
                </span>
              ))}
              {tags.length === 0 && <div className="empty-state">No tags yet.</div>}
            </div>
          </div>

          <div className="card" style={{ padding: 16, display: 'grid', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700 }}>
                <ChartBar size={18} /> Analytics
              </div>
              <select className="form-control" style={{ width: 'auto' }} value={analyticsSort} onChange={(e) => setAnalyticsSort(e.target.value)}>
                {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            {analyticsLoading ? (
              <div className="empty-state">Loading analytics...</div>
            ) : (
              <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: '#6b7280' }}>
                    <th style={{ paddingBottom: 6 }}>Title</th>
                    <th>Views</th>
                    <th>Downloads</th>
                    <th>Rating</th>
                  </tr>
                </thead>
                <tbody>
                  {analyticsItems.map((item) => (
                    <tr key={item.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '6px 0', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.title}</td>
                      <td>{item.view_count || 0}</td>
                      <td>{item.download_count || 0}</td>
                      <td>{item.average_rating != null ? item.average_rating.toFixed(1) : '—'}</td>
                    </tr>
                  ))}
                  {analyticsItems.length === 0 && (
                    <tr><td colSpan={4} className="empty-state">No data yet.</td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <LibraryCatalog
          categories={categories}
          tags={tags}
          extraParams={{ is_published: undefined }}
          showManageActions
          canManage={() => true}
          onEdit={openEditForm}
          onDelete={setDeleteTarget}
          refreshKey={refreshKey}
        />

        <LibraryItemForm
          isOpen={showForm}
          onClose={() => { setShowForm(false); setEditingItem(null); }}
          onSaved={handleSaved}
          categories={categories}
          subjects={subjects}
          classes={classes}
          editingItem={editingItem}
        />

        <DialogModal
          isOpen={Boolean(deleteTarget)}
          onClose={() => setDeleteTarget(null)}
          title="Confirm delete"
          message={deleteTarget ? `Delete "${deleteTarget.title}"? This action cannot be undone.` : ''}
          type="warning"
          primaryAction={{ label: deleteLoading ? 'Deleting...' : 'Delete', onClick: confirmDelete }}
          secondaryAction={{ label: 'Cancel', onClick: () => setDeleteTarget(null) }}
          showCloseButton={!deleteLoading}
          isLoading={deleteLoading}
          disableCloseWhenLoading={true}
        />

        </>
        )}

        <DialogModal
          isOpen={!!promptDialog}
          onClose={() => setPromptDialog(null)}
          title={promptDialog?.title}
          message={promptDialog?.message}
          type="info"
          input={{ placeholder: 'Quantity', defaultValue: promptDialog?.defaultValue || '' }}
          primaryAction={{ label: 'Submit', onClick: (value) => promptDialog?.onSubmit(value) }}
          secondaryAction={{ label: 'Cancel' }}
        />
      </div>
    </Layout>
  );
}

export default LibraryAdminPage;
